import React from "react"
import PropTypes from "prop-types"
import classNames from "classnames"

import sheetConstants from "app/static/frontend/sheets/constants"
import Tooltip from "app/static/frontend/components/Tooltip"
import { CellWrapper } from "app/static/frontend/sheets/components/cells/MantineCells/CellWrapper"

// These are used to figure out how many people to render based on the size
// of the cell without having to make calls to calculateTextWidth, which would
// be expensive if we're calling it multiple times per cell.
const PEOPLE_TO_RENDER_FACTOR = 1 / 250
const PEOPLE_TO_RENDER_ADDEND = -80

const ClickablePerson = ({ person }) => <a href={person["profile_url"]}>{person["name"]}</a>

export const CustomDataPersonsCell = ({
    delayShow,
    editing,
    isEditable,
    placement,
    width,
    height,
    cellValue,
    className,
    rowHeightFactor,
    ...props
}) => {
    const getNumberOfPeopleToRender = () => {
        if (!cellValue?.length) {
            return 0
        }
        const maxCharacters = PEOPLE_TO_RENDER_FACTOR * height * width + PEOPLE_TO_RENDER_ADDEND
        let peopleToRender = 1
        let characters = cellValue?.[0]?.["name"]?.length
        for (let i = 1; i < cellValue?.length; i++) {
            characters += cellValue?.[i]?.["name"]?.length
            if (characters > maxCharacters) {
                return peopleToRender
            }
            peopleToRender++
        }
        return peopleToRender
    }

    const renderMoreIndicator = (peopleToRender) => `... (${(cellValue?.length || 0) - peopleToRender} more)`

    const peopleToRender = getNumberOfPeopleToRender()

    const doesPeopleOverflow = peopleToRender < cellValue?.length

    const determineLabel = () => {
        if (cellValue === undefined || cellValue === null) {
            // If the cellValue is undefined or null just return an empty string
            return ""
        }

        if (typeof cellValue === "string") {
            return cellValue
        }

        return cellValue.map((p) => p?.["name"]).join(", ")
    }

    const determineChildren = () => {
        if (cellValue === undefined || cellValue === null) {
            // If the cellValue is undefined or null just return an empty string
            return ""
        }

        if (typeof cellValue === "string") {
            return cellValue
        }

        return (
            <>
                {peopleToRender >= 1
                    ? cellValue.map((p, i) => [
                          <ClickablePerson key={i} person={p} />,
                          // don't include comma on the last person.
                          i === peopleToRender - 1 ? "" : ", ",
                      ])
                    : ""}
                {doesPeopleOverflow && renderMoreIndicator(peopleToRender)}
            </>
        )
    }

    return (
        <CellWrapper {...props}>
            <Tooltip delayShow={delayShow} label={determineLabel()} placement={placement}>
                <div
                    className={classNames(
                        className,
                        "large-text-cell-overflow-hidden",
                        `large-text-cell-${rowHeightFactor || 1}`,
                    )}
                >
                    {determineChildren()}
                </div>
            </Tooltip>
        </CellWrapper>
    )
}

CustomDataPersonsCell.propTypes = {
    delayShow: PropTypes.number,
    editing: PropTypes.bool.isRequired,
    isEditable: PropTypes.bool,
    placement: PropTypes.string,
    width: PropTypes.number.isRequired,
    height: PropTypes.number.isRequired,

    // Should be an ImmutableJS list
    cellValue: PropTypes.object.isRequired,
    className: PropTypes.string,
    rowHeightFactor: PropTypes.number,
}

CustomDataPersonsCell.defaultProps = {
    delayShow: sheetConstants.CELL_TOOLTIP_DELAY,
    placement: "left",
}
